import Header from "@components/Header/Header"
import MerchantInformationCard from "@components/MerchantInformationCard/MerchantInformationCard";
import CircularButtonWithIcon from "@components/CircularButtonWithIcon/CircularButtonWithIcon";
import Chevron from "@components/Chevron/Chevron";
import PaymentLinkList from "@components/PaymentLinkList/PaymentLinkList";
import { useLocation, useNavigate } from "react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    Container,
    BackgroundImage,
    ScrollContainer,
    ContentContainer,
    CardContainer,
    TransactionContainer,
    StickyHeader,
    SectionTitleText,
    QuotationButton,
    QuotationIconContainer,
    QuotationTextContainer,
    QuotationTitle,
    QuotationSubtitle,
    ButtonWrapper,
    ButtonOverlay,
    ModalContainer
} from "./PaymentLink.styles";
import { useMerchantStore } from "@stores/merchantStore";
import useManageMerchant from "@hooks/useManageMerchant";
import LoadingModal from "@components/LoadingModal/LoadingModal";
import usePaymentLinkActivity from "@hooks/usePaymentLinkActivity";
import { PaymentLinkStatus } from "@components/PaymentLinkTransactionStatusIcon/PaymentLinkTransactionStatusIcon";
import ActivityIndicator from "@components/ActivityIndicator/ActivityIndicator";
import { hapticFeedback, setHeaderColor } from '../../lib/telegram';
import { SKY_BLUE } from "@styles/colors";
import { ModalWithIcon } from "@components/ModalWithIcon/ModalWithIcon";
import useKYCDetail from "@hooks/useKYCDetail";

// Storage key for saving scroll position
const PAYMENT_LINK_SCROLL_POSITION_KEY = 'payment_link_scroll_position';

const PaymentLink = () => {
    const { data, isLoading: isKycLoading } = useKYCDetail()
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [scrollY, setScrollY] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const lastHapticTimeRef = useRef(0);

    // Reference for the loading indicator element
    const loadingIndicatorRef = useRef<HTMLDivElement>(null)
    // Observer for infinite scrolling
    const observerRef = useRef<IntersectionObserver | null>(null)

    const { loading: merchantLoading } = useManageMerchant()
    const { name, email, profilePictureUrl } = useMerchantStore();

    const {
        data: paymentLinkActivity,
        isLoading: paymentLinkActivityLoading,
        isLoadingMore,
        loadMore,
        isReachingEnd,
        refetch
    } = usePaymentLinkActivity()

    // Handle scroll event to update background position
    const handleScroll = useCallback(() => {
        if (scrollContainerRef.current) {
            setScrollY(scrollContainerRef.current.scrollTop);
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    // Handle touch start event
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setTouchStartY(e.touches[0].clientY);
    }, []);

    // Handle touch move event
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (touchStartY === null) return;

        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;

        // Check if we're at the top of the container
        const isAtTop = scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0;

        // If user is trying to scroll up (dragging finger down) and we're at the top
        if (deltaY < 0 && isAtTop) {
            // If pull distance exceeds threshold, trigger haptic feedback and refresh
            if (Math.abs(deltaY) > 300) {
                // Get current time
                const now = Date.now();

                // Only trigger haptic feedback if it's been at least 1 second since the last trigger
                if (now - lastHapticTimeRef.current > 1000) {
                    // Trigger haptic feedback
                    hapticFeedback('heavy');
                    refetch();

                    // Update the last haptic time
                    lastHapticTimeRef.current = now;
                }
            }
        }
    }, [touchStartY, refetch]);

    // Handle touch end event
    const handleTouchEnd = useCallback(() => {
        setTouchStartY(null);
    }, []);

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        // Create the intersection observer
        observerRef.current = new IntersectionObserver(
            (entries) => {
                // If the loading indicator is visible and we're not already loading more or at the end
                if (entries[0].isIntersecting && !isLoadingMore && !isReachingEnd) {
                    loadMore();
                }
            },
            { threshold: 0.1 } // Trigger when 10% of the element is visible
        );

        // Start observing the loading indicator element
        if (loadingIndicatorRef.current) {
            observerRef.current.observe(loadingIndicatorRef.current);
        }

        return () => {
            // Clean up the observer when component unmounts
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [isLoadingMore, isReachingEnd, loadMore]);

    // Restore scroll position when component mounts
    useEffect(() => {
        if (!scrollContainerRef.current) return;

        // Get saved scroll position from sessionStorage
        const savedPosition = sessionStorage.getItem(PAYMENT_LINK_SCROLL_POSITION_KEY);

        if (savedPosition) {
            const scrollTop = parseInt(savedPosition, 10);

            // Use a small timeout to ensure the component is fully rendered
            setTimeout(() => {
                if (scrollContainerRef.current) {
                    // Direct assignment of scrollTop
                    scrollContainerRef.current.scrollTop = scrollTop;

                    // Update scrollY state to match restored position
                    setScrollY(scrollTop);

                    // Clean up after restoring
                    sessionStorage.removeItem(PAYMENT_LINK_SCROLL_POSITION_KEY);
                }
            }, 100);
        }
    }, []);

    useEffect(() => {
        setHeaderColor(SKY_BLUE)
    }, [])

    if (merchantLoading || paymentLinkActivityLoading) {
        return (
            <LoadingModal
                visible={merchantLoading || paymentLinkActivityLoading || isKycLoading}
            />)
    }

    return (
        <Container>
            <ModalContainer>
                <ModalWithIcon
                    title={t('paymentLink.kycModal.title')}
                    description={t('paymentLink.kycModal.description')}
                    positiveText={t('paymentLink.kycModal.positiveText')}
                    onPositive={() => navigate('/account-upgrade')}
                    type="kyc"
                    visible={data?.review_status !== 'APPROVED'}
                />
            </ModalContainer>
            <BackgroundImage $scrollY={scrollY} />
            <ScrollContainer
                ref={scrollContainerRef}
                className="hide-scrollbar"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onScroll={handleScroll}
            >
                <Header title={t('paymentLink.main.header')} $transparent showBackButton onBackClick={() => navigate('/home')} />
                <ContentContainer>
                    <CardContainer>
                        <MerchantInformationCard
                            title={name}
                            subtitle={email}
                            url={profilePictureUrl}
                            onClick={() => navigate('/merchant-information', {
                                state: {
                                    email: data?.email
                                }
                            })}
                        />
                        <CreateQuotationButton
                            onClick={() => navigate('/payment-link-input-amount')}
                            disabled={!name}
                        />
                    </CardContainer>

                    <TransactionContainer>
                        <StickyHeader>
                            <SectionTitle />
                        </StickyHeader>
                        <PaymentLinkList
                            activities={paymentLinkActivity?.map(activity => {
                                return {
                                    uuid: activity.uuid,
                                    amount: activity.expected_amount,
                                    status: activity.status as PaymentLinkStatus,
                                    timestamp: new Date(activity.created_at),
                                }
                            }) || []}
                            onItemClick={(activity) => {
                                // Save scroll position before navigating
                                sessionStorage.setItem(PAYMENT_LINK_SCROLL_POSITION_KEY, scrollContainerRef.current?.scrollTop.toString() || '0');

                                navigate('/transaction-detail/', {
                                    state: {
                                        from: '/payment-link',
                                        uuid: activity.uuid,
                                        ui_amount: activity.amount,
                                        status: activity.status,
                                    }
                                });
                            }}
                        />
                        {/* Loading indicator for infinite scrolling */}
                        <div ref={loadingIndicatorRef}>
                            <ActivityIndicator visible={isLoadingMore} size={24} />
                        </div>
                    </TransactionContainer>
                </ContentContainer>
            </ScrollContainer>
        </Container>
    )
}

export default PaymentLink

const SectionTitle = () => {
    const { t } = useTranslation();
    return (
        <SectionTitleText>
            {t('paymentLink.main.transactionList')}
        </SectionTitleText>
    )
}

const CreateQuotationButton = ({
    onClick,
    disabled
}: {
    onClick: () => void
    disabled?: boolean
}) => {
    const { t } = useTranslation();
    return (
        <ButtonWrapper>
            {disabled && <ButtonOverlay />}
            <QuotationButton onClick={disabled ? () => { } : onClick}>
                <QuotationIconContainer>
                    <CircularButtonWithIcon />
                    <QuotationTextContainer>
                        <QuotationTitle>
                            {t('paymentLink.main.createQuotation.title')}
                        </QuotationTitle>
                        <QuotationSubtitle>
                            {t('paymentLink.main.createQuotation.subtitle')}
                        </QuotationSubtitle>
                    </QuotationTextContainer>
                </QuotationIconContainer>
                <div>
                    <Chevron strokeWidth={1} />
                </div>
            </QuotationButton>
        </ButtonWrapper>
    )
}